import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "src/utils/googleMaps";
import { captureError } from "src/utils/errorReporting";

function toText(t) {
    if (!t) return "";
    if (typeof t === "string") return t;
    return t.text ?? "";
}

function normalize(suggestion) {
    const p = suggestion.placePrediction;
    if (!p) return null;
    return {
        placeId: p.placeId,
        description: toText(p.text),
        mainText: toText(p.mainText),
        secondaryText: toText(p.secondaryText),
        _placePrediction: p,
    };
}

export function usePlacesAutocompleteSuggestions({ country = "fi", debounceMs = 250 } = {}) {
    const [query, setQuery] = useState("");
    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const sessionTokenRef = useRef(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        let cancelled = false;
        loadGoogleMaps()
            .then((ok) => {
                if (cancelled || !ok) return;
                sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
                setReady(true);
            })
            .catch((err) => {
                captureError(err, { tags: { entity: "pumppuRequest", operation: "loadGoogleMaps" } });
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!ready) return undefined;
        const trimmed = query.trim();
        const id = ++requestIdRef.current;

        if (!trimmed) {
            const t = setTimeout(() => {
                if (id !== requestIdRef.current) return;
                setSuggestions([]);
                setLoading(false);
            }, 0);
            return () => clearTimeout(t);
        }

        const t = setTimeout(async () => {
            if (id !== requestIdRef.current) return;
            setLoading(true);
            try {
                const req = {
                    input: trimmed,
                    sessionToken: sessionTokenRef.current,
                    language: "fi",
                };
                if (country) req.includedRegionCodes = [country];
                const { suggestions: raw } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(req);
                if (id !== requestIdRef.current) return;
                setSuggestions(raw.map(normalize).filter(Boolean));
            } catch (err) {
                if (id !== requestIdRef.current) return;
                captureError(err, { tags: { entity: "pumppuRequest", operation: "fetchAutocompleteSuggestions" } });
                setSuggestions([]);
            } finally {
                if (id === requestIdRef.current) setLoading(false);
            }
        }, debounceMs);
        return () => clearTimeout(t);
    }, [query, ready, country, debounceMs]);

    function rotateSession() {
        if (window.google?.maps?.places) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
    }

    return { suggestions, loading, ready, setQuery, rotateSession };
}

export async function fetchPlaceDetails(suggestion) {
    const prediction = suggestion?._placePrediction;
    if (!prediction) throw new Error("Missing place prediction");
    const place = prediction.toPlace();
    await place.fetchFields({ fields: ["location", "formattedAddress", "addressComponents"] });
    return {
        lat: place.location?.lat(),
        lng: place.location?.lng(),
        formattedAddress: place.formattedAddress ?? "",
        addressComponents: place.addressComponents ?? [],
    };
}

// Split Google Places addressComponents into the two fields the sijainti model
// stores separately: street ("Mannerheimintie 12") and postal+city
// ("00100 Helsinki"). New Places API AddressComponent shape:
// { types: string[], longText, shortText }.
export function parseFinnishAddress(addressComponents) {
    const list = addressComponents || [];
    const longText = (type) =>
        list.find((c) => (c.types || []).includes(type))?.longText ?? "";
    const route = longText("route");
    const streetNumber = longText("street_number");
    const postalCode = longText("postal_code");
    const city =
        longText("postal_town") || longText("locality") || longText("administrative_area_level_2");
    return {
        street: [route, streetNumber].filter(Boolean).join(" ").trim(),
        postalCity: [postalCode, city].filter(Boolean).join(" ").trim(),
    };
}
